import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { serviceSchema } from '../lib/structuredData';
import { CheckCircle2, ArrowRight, Wrench, ClipboardCheck, Settings, Clock } from 'lucide-react';

const Services = () => {
  // Custom premium SVG icons — geometric, navy/orange, specific to each AC system
  const Icons = {
    WallSplit: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* wall */}
        <line x1="6" y1="14" x2="90" y2="14" />
        {/* indoor unit */}
        <rect x="20" y="20" width="56" height="22" rx="3" />
        <line x1="26" y1="28" x2="70" y2="28" />
        <line x1="26" y1="33" x2="70" y2="33" />
        {/* louvre */}
        <rect x="22" y="38" width="52" height="4" rx="1.5" stroke="#FF8C00" />
        {/* airflow */}
        <path d="M30 54 Q34 60 30 66" stroke="#FF8C00" />
        <path d="M48 56 Q52 64 48 72" stroke="#FF8C00" />
        <path d="M66 54 Q70 60 66 66" stroke="#FF8C00" />
        {/* LED dot */}
        <circle cx="68" cy="36" r="1.5" fill="#FF8C00" stroke="none" />
      </svg>
    ),
    Cassette: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* ceiling */}
        <line x1="6" y1="10" x2="90" y2="10" />
        {/* cassette frame (outer) */}
        <rect x="14" y="14" width="68" height="68" rx="3" />
        {/* inner panel */}
        <rect x="22" y="22" width="52" height="52" rx="2" />
        {/* 4 air vents */}
        <line x1="32" y1="30" x2="64" y2="30" stroke="#FF8C00" strokeWidth="2.5" />
        <line x1="32" y1="66" x2="64" y2="66" stroke="#FF8C00" strokeWidth="2.5" />
        <line x1="30" y1="32" x2="30" y2="64" stroke="#FF8C00" strokeWidth="2.5" />
        <line x1="66" y1="32" x2="66" y2="64" stroke="#FF8C00" strokeWidth="2.5" />
        {/* central grille */}
        <rect x="42" y="42" width="12" height="12" rx="1.5" />
      </svg>
    ),
    MultiSplit: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* outdoor unit (big) */}
        <rect x="36" y="62" width="40" height="22" rx="2" />
        <circle cx="56" cy="73" r="6" stroke="#FF8C00" />
        <line x1="54" y1="73" x2="58" y2="73" stroke="#FF8C00" />
        <line x1="56" y1="71" x2="56" y2="75" stroke="#FF8C00" />
        {/* 3 indoor heads */}
        <rect x="10" y="14" width="22" height="9" rx="2" />
        <rect x="64" y="14" width="22" height="9" rx="2" />
        <rect x="36" y="30" width="24" height="9" rx="2" />
        {/* refrigerant lines */}
        <path d="M21 23 L21 50 L48 50 L48 62" stroke="#FF8C00" />
        <path d="M75 23 L75 56 L60 56 L60 62" stroke="#FF8C00" />
        <path d="M48 39 L48 50" stroke="#FF8C00" />
      </svg>
    ),
    VRV: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* building outline */}
        <path d="M16 86 L16 16 L56 16 L56 30 L80 30 L80 86 Z" />
        {/* roof outdoor units (VRF condensers) */}
        <rect x="20" y="6" width="10" height="10" rx="1" stroke="#FF8C00" />
        <rect x="34" y="6" width="10" height="10" rx="1" stroke="#FF8C00" />
        <circle cx="25" cy="11" r="2" stroke="#FF8C00" />
        <circle cx="39" cy="11" r="2" stroke="#FF8C00" />
        {/* zone windows */}
        <rect x="24" y="28" width="8" height="8" />
        <rect x="40" y="28" width="8" height="8" />
        <rect x="24" y="44" width="8" height="8" />
        <rect x="40" y="44" width="8" height="8" />
        <rect x="24" y="60" width="8" height="8" />
        <rect x="40" y="60" width="8" height="8" />
        <rect x="62" y="42" width="10" height="8" />
        <rect x="62" y="56" width="10" height="8" />
        <rect x="62" y="70" width="10" height="8" />
        {/* riser line */}
        <line x1="14" y1="16" x2="14" y2="86" stroke="#FF8C00" />
      </svg>
    ),
    Ducted: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* ceiling line */}
        <line x1="4" y1="22" x2="92" y2="22" />
        {/* horizontal duct */}
        <rect x="10" y="26" width="76" height="14" rx="2" />
        {/* duct segments */}
        <line x1="28" y1="26" x2="28" y2="40" />
        <line x1="48" y1="26" x2="48" y2="40" />
        <line x1="68" y1="26" x2="68" y2="40" />
        {/* drop vents */}
        <rect x="18" y="44" width="14" height="6" rx="1" stroke="#FF8C00" />
        <rect x="44" y="44" width="14" height="6" rx="1" stroke="#FF8C00" />
        <rect x="68" y="44" width="14" height="6" rx="1" stroke="#FF8C00" />
        {/* airflow arrows */}
        <path d="M25 56 V72 M22 68 L25 72 L28 68" stroke="#FF8C00" />
        <path d="M51 56 V72 M48 68 L51 72 L54 68" stroke="#FF8C00" />
        <path d="M75 56 V72 M72 68 L75 72 L78 68" stroke="#FF8C00" />
      </svg>
    ),
    Portable: (
      <svg viewBox="0 0 96 96" className="w-20 h-20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {/* window frame */}
        <line x1="72" y1="6" x2="72" y2="76" />
        <line x1="72" y1="6" x2="90" y2="6" />
        <line x1="72" y1="76" x2="90" y2="76" />
        {/* portable unit body */}
        <rect x="14" y="32" width="44" height="46" rx="3" />
        {/* control panel */}
        <rect x="20" y="38" width="32" height="8" rx="1" />
        <circle cx="48" cy="42" r="1.5" fill="#FF8C00" stroke="none" />
        {/* vent grille */}
        <line x1="20" y1="54" x2="52" y2="54" stroke="#FF8C00" />
        <line x1="20" y1="58" x2="52" y2="58" stroke="#FF8C00" />
        <line x1="20" y1="62" x2="52" y2="62" stroke="#FF8C00" />
        {/* wheels */}
        <circle cx="20" cy="82" r="3" />
        <circle cx="52" cy="82" r="3" />
        {/* exhaust hose to window */}
        <path d="M58 38 Q66 38 66 30 Q66 22 72 22" stroke="#FF8C00" />
        <path d="M60 38 Q68 38 68 30 Q68 24 72 24" stroke="#FF8C00" strokeDasharray="2 2" />
      </svg>
    ),
  };

  const installationTypes = [
    { name: 'Wall Mounted Systems', desc: 'Perfect for single rooms and smaller spaces — fast install, clean lines.', icon: Icons.WallSplit },
    { name: 'Cassette Systems', desc: 'Discreet ceiling-mounted units for offices, retail and premium residential.', icon: Icons.Cassette },
    { name: 'Multi-Split Systems', desc: 'One outdoor unit serving multiple indoor heads — ideal for multi-room homes.', icon: Icons.MultiSplit },
    { name: 'VRV / VRF Systems', desc: 'Advanced multi-zone systems for large commercial buildings and offices.', icon: Icons.VRV },
    { name: 'Ducted Systems', desc: 'Concealed in-ceiling ductwork with discreet grilles — invisible, whisper-quiet.', icon: Icons.Ducted },
    { name: 'Portable Units', desc: 'Flexible plug-and-play cooling for short-term needs and rental spaces.', icon: Icons.Portable },
  ];

  const installationProcess = [
    { step: '1', title: 'Consultation', desc: 'We discuss your needs & recommend the right solution for your space.' },
    { step: '2', title: 'Site Survey', desc: 'Our engineers assess your space and identify the best positioning.' },
    { step: '3', title: 'Quotation', desc: 'Transparent quote with no hidden costs or unexpected fees.' },
    { step: '4', title: 'Installation', desc: 'Professional installation by F-Gas qualified engineers.' },
    { step: '5', title: 'Commissioning', desc: 'We test and commission your system to peak performance.' },
  ];

  const serviceFeatures = [
    'Domestic & Commercial Installations',
    'High-Efficiency Systems',
    'Clean & Professional Workmanship',
    '5 Year Installation Guarantee',
    'Lower Running Costs',
    'Extended Warranty Options',
    'F-Gas Compliant Installation',
    'Flexible Payment Plans',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Air Conditioning Installation London"
        description="Complete air conditioning installation across London — wall-mounted splits, multi-splits, ceiling cassettes, ducted systems, VRV / VRF. F-Gas compliant. 5-year guarantee."
        path="/installation"
        jsonLd={serviceSchema(
          'Air Conditioning Installation',
          'Wall-mounted splits, multi-splits, ceiling cassettes, ducted systems and commercial VRV/VRF installed across London.'
        )}
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.70)), url('/images/hero-services.jpg')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-block text-xs bg-white/10 px-4 py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/30 tracking-wider mb-6">
            OUR SERVICES
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            Air Conditioning <span className="text-[#FF8C00]">Installation</span> London
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
            Professional supply and installation of high-efficiency air conditioning systems for homes and commercial properties across London.
          </p>
        </div>
      </section>

      {/* Installation Features */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceFeatures.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 size={24} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
                <span className="text-base font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Types */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">INSTALLATION SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-bold">Types of AC Systems We Install</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {installationTypes.map((type, idx) => (
              <div
                key={idx}
                className="group relative bg-[#0A0E14] border border-white/10 rounded-2xl p-8 flex flex-col items-center text-center transition-all duration-300 hover:border-[#FF8C00]/50 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#FF8C00]/10"
                data-testid={`service-type-${idx}`}
              >
                {/* Icon */}
                <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#FF8C00]/10 to-[#FF8C00]/0 border border-white/5 grid place-items-center mb-6 text-white group-hover:text-[#FF8C00] group-hover:border-[#FF8C00]/30 transition-colors">
                  {type.icon}
                </div>

                <h3 className="text-xl font-bold mb-2 group-hover:text-[#FF8C00] transition-colors">{type.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">OUR INSTALLATION PROCESS</p>
            <h2 className="text-4xl md:text-5xl font-bold">How We Work</h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {installationProcess.map((item, idx) => (
              <div key={idx} className="text-center relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF8C00] flex items-center justify-center text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {idx < installationProcess.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-3 text-[#FF8C00]" size={20} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to get started?</h2>
          <p className="text-gray-400 text-lg mb-8">Get your free instant quote today.</p>
          <Link to="/ai-estimator">
            <Button className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold px-10 py-6 rounded-md text-base" data-testid="services-cta-quote">
              Get Instant Quote <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
